const en = {
  app: {
    name: 'Indabo za Mariya',
    tagline: 'Parish Management',
    subtitle: 'Parish Management System',
  },
  common: {
    search: 'Search',
    filter: 'Filter',
    all: 'All',
    actions: 'Actions',
    view: 'View',
    edit: 'Edit',
    delete: 'Delete',
    deactivate: 'Deactivate',
    activate: 'Activate',
    cancel: 'Cancel',
    save: 'Save',
    create: 'Create',
    confirm: 'Confirm',
    close: 'Close',
    export: 'Export',
    download: 'Download',
    yes: 'Yes',
    no: 'No',
    optional: 'optional',
    required: 'required',
    loading: 'Loading...',
    saving: 'Saving...',
    deleting: 'Deleting...',
    submit: 'Submit',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    none: 'None',
    select_placeholder: 'Select...',
    no_results: 'No results',
    no_data: 'No data to display',
    apply: 'Apply',
    reset: 'Reset',
    open_menu: 'Open menu',
    close_menu: 'Close menu',
    language: 'Language',
    page_of: 'Page {current} of {total}',
    showing_results: '{from}–{to} of {total} results',
    name: 'Name',
    full_name: 'Full name',
    phone: 'Phone',
    email: 'Email',
    role: 'Role',
    notes: 'Notes',
    date: 'Date',
    status: 'Status',
    type: 'Type',
    amount: 'Amount',
    reference: 'Reference',
    method: 'Method',
    event: 'Event',
    total: 'Total',
    currency_rwf: 'RWF',
    refresh: 'Refresh',
    api_required: 'This page uses live backend data. Set REACT_APP_API_URL to enable.',
    server_secret_hint: 'These integrations are configured as server secrets (environment variables) — they cannot be changed from this screen.',
  },
  nav: {
    section_main: 'Main',
    section_management: 'Management',
    section_analysis: 'Analysis',
    section_admin: 'Administration',
    dashboard: 'Dashboard',
    members: 'Members',
    events: 'Pilgrimages',
    payments: 'Payments',
    reports: 'Reports',
    users: 'Users',
    roles: 'Roles & Permissions',
    audit: 'Audit log',
    profile: 'My profile',
    logout: 'Sign out',
    reservations: 'Reservations',
    transport: 'Transport',
    finance: 'Finance',
    payouts: 'Payouts',
    documents: 'Minutes & reports',
    settings: 'Settings',
  },
  pages: {
    finance: { title: 'Finance', subtitle: 'Income & expense records' },
    dashboard: { title: 'Dashboard', subtitle: 'General overview' },
    members: { title: 'Members', subtitle: 'Registered faithful' },
    events: { title: 'Pilgrimages', subtitle: 'Events & registrations' },
    payments: { title: 'Finance', subtitle: 'Contributions & offerings' },
    reports: { title: 'Reports', subtitle: 'PDF & Excel exports' },
    users: { title: 'Users & Roles', subtitle: 'Access management' },
    not_found: { title: 'Page not found', subtitle: 'The page you requested does not exist.' },
    forbidden: { title: 'Access denied', subtitle: 'You do not have permission to view this page.' },
    roles: { title: 'Roles & Permissions', subtitle: 'Per-role access definitions' },
    audit: { title: 'Audit log', subtitle: 'Full history of user actions' },
    profile: { title: 'My profile', subtitle: 'Account and security' },
    reservations: { title: 'Reservations', subtitle: 'Website bookings & online payments' },
    transport: { title: 'Transport', subtitle: 'Buses, pick-up points & assignments' },
    payouts: { title: 'Payouts', subtitle: 'Collected funds & withdrawals' },
    documents: { title: 'Minutes & reports', subtitle: 'Meeting minutes, reports & announcements' },
    settings: { title: 'Settings', subtitle: 'Integrations, fees & payout destination' },
  },
  auth: {
    login_title: 'Sign in',
    username: 'Username',
    password: 'Password',
    username_placeholder: 'Enter your username',
    password_placeholder: 'Enter your password',
    sign_in: 'Sign In',
    signing_in: 'Signing in...',
    fill_credentials: 'Please enter your credentials',
    invalid_credentials: 'Invalid credentials',
    verse: '"I am the way, the truth and the life." — John 14:6',
    logged_in: 'Welcome, {name}',
    logged_out: 'You have been signed out',
  },
  dashboard: {
    total_members: 'Total Members',
    active_members: 'Active Members',
    pilgrims_registered: 'Pilgrims Registered',
    total_revenue: 'Total Revenue',
    this_month_added: '↑ {count} this month',
    percent_of_total: '{percent}% of total',
    this_week_added: '↑ {count} this week',
    revenue_year: 'RWF — this year',
    monthly_revenue: 'Monthly Revenue',
    monthly_revenue_sub: 'Contributions, pilgrimages & offerings',
    recent_payments: 'Recent Payments',
    upcoming_events: 'Upcoming Pilgrimages',
    recent_activity: 'Recent Activity',
    spots_left: 'spots',
    booked: 'booked',
    quick_actions: 'Quick Actions',
    record_payment: 'Record Payment',
    new_member: 'New Member',
    new_event: 'New Event',
    generate_report: 'Generate Report',
  },
  members: {
    title: 'Member Registry',
    subtitle: '{count} faithful registered',
    new_member: 'New Member',
    edit_member: 'Edit Member',
    view_member: 'Member Details',
    delete_confirm_title: 'Delete this member?',
    delete_confirm_body: 'This action is irreversible. Member {name} will be removed from the registry.',
    search_placeholder: 'Search by name, ID, phone, parish...',
    filter_status: 'All statuses',
    field_first_name: 'First name',
    field_last_name: 'Last name',
    field_phone: 'Phone',
    field_email: 'Email',
    field_address: 'Address',
    field_joined_at: 'Joined on',
    field_status: 'Status',
    field_notes: 'Notes',
    field_total_paid: 'Total Paid',
    field_id: 'ID',
    statuses: {
      active: 'Active',
      inactive: 'Inactive',
      pending: 'Pending',
    },
    toast_created: 'Member {name} added',
    toast_updated: 'Member {name} updated',
    toast_deleted: 'Member deleted',
    empty_title: 'No members found',
    empty_body: 'Add your first member to start the registry.',
    field_parish: 'Parish',
    field_gender: 'Gender',
    field_marital_status: 'Marital status',
    field_national_id: 'National ID / passport',
    field_date_of_birth: 'Date of birth',
    national_id_hint: 'Optional — 16-digit national ID or passport number (6–20 letters/digits)',
    national_id_invalid: 'Use 6 to 20 letters or digits',
    dob_in_past: 'Date of birth must be in the past',
    not_specified: 'Not specified',
    genders: {
      male: 'Male',
      female: 'Female',
    },
    marital_statuses: {
      single: 'Single',
      married: 'Married',
      widowed: 'Widowed',
      divorced: 'Divorced',
      religious: 'Religious (consecrated)',
    },
  },
  events: {
    title: 'Pilgrimages & Events',
    subtitle: 'Spiritual journeys management',
    new_event: 'New Event',
    edit_event: 'Edit Event',
    view_event: 'Event Details',
    delete_confirm_title: 'Delete this event?',
    delete_confirm_body: 'This action is irreversible. Event {name} will be deleted.',
    search_placeholder: 'Search events...',
    filter_status: 'All statuses',
    field_name: 'Event name',
    field_destination: 'Destination',
    field_date: 'Date',
    field_capacity: 'Capacity',
    field_registered: 'Registered',
    field_price: 'Ticket Price',
    field_status: 'Status',
    field_description: 'Description',
    field_reference: 'Reference',
    registered_count: '{count} ({percent}%)',
    statuses: {
      open: 'Open',
      closed: 'Closed',
      completed: 'Completed',
      cancelled: 'Cancelled',
    },
    toast_created: 'Event {name} created',
    toast_updated: 'Event {name} updated',
    toast_deleted: 'Event deleted',
    monthly_badge: 'Monthly · last Sunday',
    no_limit: 'No limit',
    capacity_hint: 'Optional — leave empty or 0 for no limit',
    field_booking: 'Booking',
    booking_open: 'Booking open',
    booking_reopened: 'Reopened',
    booking_closed: 'Booking closed',
    booking_closes: 'Closes {when}',
    booking_until: 'Open until {when}',
    action_reopen_booking: 'Reopen booking',
    action_close_booking: 'Close booking',
    toast_booking_reopened: 'Booking reopened for {name}',
    toast_booking_closed: 'Booking closed again for {name}',
    empty_title: 'No events yet',
    empty_body: 'Create your first pilgrimage to begin.',
  },
  payments: {
    title: 'Financial Transactions',
    subtitle: 'Complete payment history',
    new_payment: 'Record Payment',
    record_payment: 'Record',
    short_label: 'Payment',
    edit_payment: 'Edit Payment',
    view_payment: 'Payment Details',
    delete_confirm_title: 'Delete this payment?',
    delete_confirm_body: 'This action is irreversible. Payment {ref} will be deleted.',
    search_placeholder: 'Search by member, reference...',
    filter_type: 'All types',
    filter_status: 'All statuses',
    total_collected: 'Total Collected',
    pending: 'Pending',
    this_month: 'This Month',
    field_member: 'Member',
    field_payer: 'Member or payer',
    payer_hint: 'Select a member or type a name',
    payer_guest_action: 'Record for "{name}" (non-member)',
    payer_guest_label: 'Non-member',
    payer_required: 'Select a member or type a name',
    payer_search_empty: 'No member found',
    field_type: 'Type',
    field_amount: 'Amount',
    field_date: 'Date',
    field_method: 'Method',
    field_event: 'Event',
    field_status: 'Status',
    field_notes: 'Notes',
    total_all_time: 'RWF — All time',
    pending_caption: 'Payments to confirm',
    this_month_caption: 'RWF — Current month',
    types: {
      cotisation: 'Contribution',
      pelerinage: 'Pilgrimage',
      offrande: 'Offering',
    },
    methods: {
      mobile_money: 'Mobile Money',
      bank_transfer: 'Bank Transfer',
      cash: 'Cash',
      card: 'Card',
    },
    statuses: {
      paid: 'Paid',
      pending: 'Pending',
      cancelled: 'Cancelled',
      refunded: 'Refunded',
    },
    toast_created: 'Payment {ref} recorded',
    toast_updated: 'Payment {ref} updated',
    toast_deleted: 'Payment deleted',
    toast_marked_paid: 'Payment {ref} confirmed',
    mark_as_paid: 'Mark as paid',
    empty_title: 'No payments',
    empty_body: 'Record your first payment to start.',
    field_external_ref: 'Gateway reference',
  },
  reports: {
    section_title: 'Reports & Exports',
    section_subtitle: 'Generate and download reports as PDF or Excel',
    cards: {
      members_title: 'Members Report',
      members_text: 'Total, active, inactive and contribution history',
      events_title: 'Pilgrimages Report',
      events_text: 'Pilgrims per event, revenues, available spots',
      finance_title: 'Financial Statement',
      finance_text: 'Revenues by type, monthly summary, full history',
      monthly_title: 'Monthly Report',
      monthly_text: 'Detailed financial breakdown for a selected month',
      member_history_title: 'Member History',
      member_history_text: 'All payments recorded for a faithful',
      official_title: 'Official Report',
      official_text: 'Formatted report for submission to authorities',
    },
    generate_title: 'Generate report',
    select_period: 'Period',
    period_all: 'All time',
    period_year: 'This year',
    period_month: 'This month',
    format_pdf: 'PDF',
    format_excel: 'Excel',
    toast_generated: 'Report {name} ready — download started',
  },
  users: {
    title: 'System Users',
    subtitle: 'Roles and permissions',
    new_user: 'Add User',
    edit_user: 'Edit User',
    deactivate_confirm_title: 'Deactivate this user?',
    deactivate_confirm_body: 'User {name} will no longer be able to sign in.',
    activate_confirm_title: 'Reactivate this user?',
    activate_confirm_body: 'User {name} will be able to sign in again.',
    search_placeholder: 'Search users...',
    field_username: 'Username',
    field_role: 'Role',
    field_phone: 'Phone',
    field_last_login: 'Last login',
    field_password: 'Password',
    field_password_confirm: 'Confirm password',
    password_mismatch: 'Passwords do not match',
    password_hint: 'Leave empty to keep the current password',
    roles: {
      admin: 'Administrator',
      tresorier: 'Treasurer',
      coordinateur: 'Coordinator',
      secretaire: 'Secretary',
    },
    toast_created: 'User {name} added',
    toast_updated: 'User {name} updated',
    toast_deactivated: 'User {name} deactivated',
    toast_activated: 'User {name} reactivated',
  },
  modal: {
    unsaved_title: 'Unsaved changes',
    unsaved_body: 'Your changes will be lost. Continue?',
  },
  forbidden: {
    back_home: 'Back to dashboard',
    need_permission: 'Permission required: {permission}',
  },
  profile: {
    section_account: 'Account information',
    section_security: 'Security',
    change_password: 'Change password',
    current_password: 'Current password',
    new_password: 'New password',
    confirm_new_password: 'Confirm new password',
    password_min: 'Minimum 8 characters',
    password_mismatch: 'Passwords do not match',
    toast_password_changed: 'Password updated',
    last_login: 'Last login',
    member_since: 'Member since',
    api_mode_off: 'API is not configured — this feature requires the Spring Boot backend.',
  },
  roles_page: {
    new_role: 'New role',
    edit_role: 'Edit role',
    system_role: 'System role',
    field_name: 'Role name',
    field_description: 'Description',
    field_permissions: 'Permissions',
    user_count: '{count} users',
    delete_confirm_title: 'Delete this role?',
    delete_confirm_body: 'Role {name} will be deleted. This action is irreversible.',
    toast_created: 'Role {name} created',
    toast_updated: 'Role {name} updated',
    toast_deleted: 'Role deleted',
    permissions_count: '{count} permissions',
    api_required: 'This page uses live backend data. Set REACT_APP_API_URL to enable.',
  },
  audit_page: {
    search_placeholder: 'Search action, user...',
    field_when: 'When',
    field_user: 'User',
    field_action: 'Action',
    field_entity: 'Entity',
    field_description: 'Description',
    field_ip: 'IP',
    api_required: 'This page uses live backend data. Set REACT_APP_API_URL to enable.',
  },
  notifications: {
    title: 'Notifications',
    empty: 'No recent activity',
    view_all: 'View all',
  },
  toast: {
    success: 'Success',
    error: 'Error',
    info: 'Information',
    warning: 'Warning',
    generic_error: 'Something went wrong',
    not_implemented: 'This feature is coming soon',
  },
  activity: {
    new_member: 'New member registered: {name}',
    payment_confirmed: 'Payment {ref} confirmed by treasurer',
    event_updated: 'Pilgrimage {name} — capacity updated',
    pilgrims_registered: '{count} new pilgrims registered for {name}',
    report_exported: 'Monthly report exported by treasurer',
  },
  time: {
    just_now: 'Just now',
    minutes_ago: '{count} min ago',
    hours_ago: '{count} h ago',
    yesterday: 'Yesterday',
    days_ago: '{count} days ago',
  },
  months: {
    short: {
      jan: 'Jan',
      feb: 'Feb',
      mar: 'Mar',
      apr: 'Apr',
      may: 'May',
      jun: 'Jun',
      jul: 'Jul',
      aug: 'Aug',
      sep: 'Sep',
      oct: 'Oct',
      nov: 'Nov',
      dec: 'Dec',
    },
    abbr: {
      jan: 'JAN',
      feb: 'FEB',
      mar: 'MAR',
      apr: 'APR',
      may: 'MAY',
      jun: 'JUN',
      jul: 'JUL',
      aug: 'AUG',
      sep: 'SEP',
      oct: 'OCT',
      nov: 'NOV',
      dec: 'DEC',
    },
  },
  reservations_page: {
    field_member: 'Community member',
    member_yes: 'Member',
    title: 'Website reservations',
    subtitle: 'Bookings made on the public website',
    search_placeholder: 'Search by code, name, phone...',
    filter_status: 'All statuses',
    filter_event: 'All pilgrimages',
    view_title: 'Reservation details',
    field_contact: 'Contact',
    field_payer: 'Paying number',
    field_pilgrimage: 'Pilgrimage',
    field_people: 'People',
    field_parish: 'Parish',
    field_emergency: 'Emergency phone',
    field_pickup: 'Pick-up point',
    field_method: 'Payment method',
    field_customer_ref: 'Customer reference',
    field_gateway_ref: 'Gateway reference',
    field_gateway_tid: 'Gateway transaction ID',
    field_gateway_status: 'Gateway status',
    field_gateway_message: 'Gateway message',
    field_linked_payment: 'Linked payment',
    field_paid_at: 'Paid on',
    field_created_at: 'Created on',
    section_contact: 'Contact person',
    section_participants: 'Travellers ({count})',
    section_payment: 'Payment',
    amount_breakdown: '{people} × ({unit} + {fee} fee)',
    statuses: {
      pending_payment: 'Awaiting payment',
      confirmed: 'Confirmed',
      failed: 'Failed',
      cancelled: 'Cancelled',
    },
    methods: {
      momo: 'Mobile Money',
      card: 'Card',
    },
    stat_confirmed: 'Confirmed',
    stat_confirmed_caption: 'Paid reservations',
    stat_pending: 'Awaiting payment',
    stat_pending_caption: 'Waiting for MoMo approval',
    stat_failed: 'Failed',
    stat_failed_caption: '{count} cancelled',
    stat_collected: 'Collected',
    stat_collected_caption: 'RWF — confirmed payments',
    stat_travellers: 'Travellers',
    stat_travellers_caption: 'Confirmed seats',
    recheck: 'Re-check payment',
    cancel_reservation: 'Cancel reservation',
    cancel_confirm_title: 'Cancel this reservation?',
    cancel_confirm_body: 'Reservation {code} will be marked as cancelled. Only unpaid reservations can be cancelled.',
    toast_rechecked: '{code}: {status}',
    toast_cancelled: 'Reservation {code} cancelled',
    gateway_missing_title: 'XentriPay is not configured on the server',
    gateway_missing_body: 'Online payments from the website are disabled until the XentriPay API key is set as a server secret.',
    empty_title: 'No reservations',
    empty_body: 'Bookings made on the public website will appear here.',
  },
  payouts_page: {
    collected: 'Collected',
    collected_caption: 'RWF — confirmed online payments',
    paid_out: 'Paid out',
    paid_out_caption: 'RWF — payouts sent',
    available: 'Available (estimate)',
    available_caption: 'XentriPay deducts its collection fees',
    destination_title: 'Payout destination',
    destination_subtitle: 'The only number that can receive payouts',
    updated: 'updated {when}',
    no_number_title: 'No payout number configured',
    no_number_body: 'Set the mobile money number that will receive payouts before sending any funds.',
    change_number: 'Change number',
    set_number: 'Set payout number',
    admin_only: 'Only the administrator (with the payouts:manage permission) can do this.',
    mail_missing: 'E-mail (SMTP) is not configured, so verification codes cannot be sent.',
    gateway_missing: 'XentriPay is not configured on the server.',
    blocked_no_number: 'Set a payout number first.',
    number_change_notice: 'For security, a verification code will be e-mailed to the administrator. The new number only takes effect once that code is confirmed.',
    field_msisdn: 'Mobile money number',
    msisdn_hint: 'Rwandan format, e.g. 2507XXXXXXXX',
    field_name: 'Account holder name',
    name_hint: 'As registered with the mobile money operator',
    field_provider: 'Operator',
    provider_auto: 'Detect from number',
    providers: {
      mtn: 'MTN Mobile Money',
      airtel: 'Airtel Money',
    },
    confirm_number_title: 'Confirm new payout number',
    confirm_number: 'Confirm number',
    new_destination: 'New destination',
    toast_number_updated: 'Payout number updated',
    send_title: 'Send funds',
    send_subtitle: 'Withdraw collected money',
    send_body: 'Transfers part of the collected balance to the configured payout number. Each payout must be confirmed with a code e-mailed to the administrator.',
    new_payout: 'New payout',
    new_payout_sub: 'Funds are sent to the configured number only',
    field_amount: 'Amount (RWF)',
    field_note: 'Note',
    field_recipient: 'Recipient',
    field_requested_by: 'Requested by',
    amount_integer: 'Enter a whole amount in RWF',
    amount_min: 'The minimum payout is {min}',
    amount_exceeds: 'Amount exceeds the available balance ({available})',
    confirm_heading: 'Before you continue',
    confirm_only_number: 'The money will be sent ONLY to {number} — it cannot be redirected to another number.',
    confirm_email_code: 'A 6-digit code will be e-mailed to the administrator to confirm this payout.',
    confirm_xentripay_otp: 'XentriPay will then ask the account holder to approve the transfer with its own OTP before the funds are released.',
    confirm_estimate: 'The available balance is an estimate: XentriPay deducts its collection fees.',
    confirm_payout_title: 'Confirm payout',
    send_payout: 'Send payout',
    result_title: 'Payout submitted',
    result_failed: 'The gateway rejected this payout',
    result_failed_generic: 'No funds were sent. Check the gateway status and try again.',
    result_submitted: 'Payout recorded',
    result_approve_hint: 'The holder of {number} must now approve the transfer with the XentriPay OTP. Use "Re-check" to refresh its status.',
    history_title: 'Payout history',
    history_subtitle: 'All withdrawals sent through XentriPay',
    history_empty: 'No payouts yet',
    recheck: 'Re-check',
    toast_rechecked: '{code}: {status}',
    statuses: {
      pending: 'Pending',
      completed: 'Completed',
      successful: 'Successful',
      failed: 'Failed',
      reversed: 'Reversed',
    },
  },
  otp: {
    title: 'Verification code',
    sent_to: 'We sent a 6-digit code to {email}.',
    sent_generic: 'We sent a 6-digit code to the administrator e-mail.',
    code_label: 'Verification code',
    invalid_code: 'Enter the 6-digit code',
    expires_in: 'Code expires in {time}',
    expired: 'This code has expired — request a new one.',
    resend: 'Send a new code',
    resent: 'A new code has been sent',
    send_code: 'Send verification code',
  },
  monthly: {
    title: 'Monthly pilgrimage',
    subtitle: 'Created automatically for the last Sunday of every month',
    status_on: 'Automatic',
    status_off: 'Paused',
    rule_title: 'How booking works',
    rule_body: 'A pilgrimage is created for the last Sunday of each month. Booking and payment close on the Saturday before at {close}. Reopen booking from the Pilgrimages list to keep it open until departure on Sunday at {departure}.',
    upcoming_booked: '{code} · {registered} booked',
    field_places: 'Places',
    field_places_hint: 'Everyone who books while booking is open is accepted',
    upcoming_pending: 'Will be created automatically',
    booking_open: 'Booking open',
    booking_closed: 'Booking closed',
    closes_at: 'Closes {when}',
    open_until: 'Reopened until {when}',
    field_enabled: 'Create the last-Sunday pilgrimage automatically every month',
    field_name: 'Pilgrimage name',
    field_name_hint: 'The month is added automatically, e.g. “… — November 2026”',
    field_destination: 'Destination',
    field_price: 'Price per person',
    field_price_hint: 'Saved price: {price}. It stays until you change it.',
    field_close_time: 'Booking closes (Saturday)',
    field_close_time_hint: '00:00 = closes as Saturday begins',
    field_departure_time: 'Departure (Sunday)',
    field_departure_time_hint: 'Reopened bookings stay open until then',
    field_months_ahead: 'Months ahead',
    field_months_ahead_hint: 'How many future months to open (0–6)',
    field_description: 'Description',
    field_description_hint: 'Shown on the website reservation card',
    price_invalid: 'Enter a whole amount of at least 100 RWF',
    months_invalid: 'Enter a number between 0 and 6',
    time_invalid: 'Use HH:mm',
    toast_saved: 'Monthly pilgrimage saved — upcoming pilgrimages updated',
    admin_only: 'Only the administrator can change these settings.',
  },
  finance: {
    local_title: 'Preview — saved on this computer',
    local_body: 'Entries are kept in this browser until the finance ledger is connected to the server. Export a CSV to keep a copy.',
    clear_samples: 'Remove sample entries',
    clear_samples_title: 'Remove the sample entries?',
    clear_samples_body: 'The example transactions added for the demo will be deleted. Your own entries are kept.',
    period: 'Period',
    periods: { this_month: 'This month', last_month: 'Last month', this_year: 'This year', all: 'All time', custom: 'Custom' },
    period_range: '{from} – {to}',
    from: 'From',
    to: 'To',
    export_csv: 'Export CSV',
    print_report: 'Print / PDF',
    total_income: 'Income',
    total_expense: 'Expenses',
    net_balance: 'Balance',
    surplus: 'Surplus for the period',
    deficit: 'Deficit for the period',
    record_income: 'Record income',
    record_income_hint: 'Money received — contributions, offerings, fees…',
    record_expense: 'Record expense',
    record_expense_hint: 'Money spent — transport, supplies, support…',
    search_placeholder: 'Search source, destination, reference…',
    all_types: 'Income & expenses',
    all_categories: 'All categories',
    ledger_title: 'Transactions',
    ledger_subtitle: '{count} entries · {period}',
    empty_title: 'No transactions in this period',
    empty_body: 'Record an income or an expense to start the ledger.',
    new_entry: 'New transaction',
    edit_entry: 'Edit transaction',
    form_subtitle: 'Where the money came from and where it went',
    record: 'Record',
    amount_invalid: 'Enter an amount greater than 0',
    field_type: 'Type',
    field_date: 'Date',
    field_amount: 'Amount',
    field_category: 'Category',
    field_method: 'Payment method',
    field_source_income: 'Received from',
    field_source_expense: 'Paid from',
    field_destination_income: 'Deposited into',
    field_destination_expense: 'Paid to',
    hint_source_income: 'Who gave the money, e.g. members, a benefactor',
    hint_source_expense: 'Which account or cash box paid',
    hint_destination_income: 'Where it was kept, e.g. cash box, MoMo, bank',
    hint_destination_expense: 'Who received it, e.g. supplier, family',
    field_reference: 'Reference',
    hint_reference: 'Receipt, invoice or transaction number',
    field_description: 'Description',
    col_code: 'No.',
    col_flow: 'From → To',
    col_from: 'From',
    col_to: 'To',
    col_recorded_by: 'Recorded by',
    breakdown_income: 'Income by category',
    breakdown_expense: 'Expenses by category',
    breakdown_empty: 'Nothing recorded in this period.',
    report_title: 'Financial report',
    report_generated: 'Generated on {date} by {name}',
    report_transactions: 'Transactions',
    delete_title: 'Delete this transaction?',
    delete_body: 'Transaction {code} will be removed from the ledger.',
    toast_income: 'Income recorded',
    toast_expense: 'Expense recorded',
    toast_updated: 'Transaction updated',
    toast_deleted: 'Transaction deleted',
    toast_samples_cleared: 'Sample entries removed',
    toast_exported: '{count} transactions exported',
    types: { income: 'Income', expense: 'Expense' },
    methods: { cash: 'Cash', mobile_money: 'Mobile Money', bank_transfer: 'Bank transfer', card: 'Card', cheque: 'Cheque' },
    categories: {
      contributions: 'Member contributions',
      offerings: 'Offerings & donations',
      pilgrimage_fees: 'Pilgrimage fees',
      fundraising: 'Fundraising',
      grants: 'Grants & support',
      other_income: 'Other income',
      transport: 'Transport',
      accommodation: 'Accommodation',
      food: 'Food & catering',
      liturgy: 'Liturgy & church supplies',
      communication: 'Printing & communication',
      charity: 'Charity & support',
      allowances: 'Allowances',
      fees: 'Bank & MoMo fees',
      equipment: 'Equipment',
      other_expense: 'Other expense',
    },
  },
  settings_page: {
    integrations_title: 'Integration health',
    integrations_subtitle: 'Payment gateway, webhook & e-mail',
    integration_gateway: 'XentriPay API key',
    integration_gateway_hint: 'Required to collect website payments and send payouts',
    integration_webhook: 'XentriPay webhook secret',
    integration_webhook_hint: 'Lets the gateway confirm payments automatically',
    integration_mail: 'SMTP e-mail',
    integration_mail_hint: 'Sends verification codes and confirmations',
    configured: 'Configured',
    missing: 'Missing',
    fee_title: 'Reservation fee',
    fee_subtitle: 'Transaction fee added per traveller',
    fee_label: 'Fee per person',
    fee_hint: 'Added to each traveller on website reservations (0 – 100,000 RWF)',
    fee_invalid: 'Enter a whole amount between 0 and 100,000',
    toast_fee_saved: 'Reservation fee set to {fee}',
  },
  transport_page: {
    tab_buses: 'Buses',
    tab_locations: 'Departure locations',
    tab_assignments: 'Assignments',
    buses_title: 'Fleet',
    buses_subtitle: 'Registered buses and drivers',
    buses_empty: 'No buses registered',
    locations_title: 'Departure locations',
    locations_subtitle: 'Pick-up points offered to travellers',
    locations_empty: 'No departure locations',
    search_buses: 'Search plate, driver...',
    search_locations: 'Search locations...',
    new_bus: 'New bus',
    edit_bus: 'Edit bus',
    new_location: 'New location',
    edit_location: 'Edit location',
    field_plate: 'Plate number',
    field_bus_name: 'Name',
    bus_name_hint: 'Optional — e.g. "Coaster 1"',
    field_capacity: 'Seats',
    field_driver: 'Driver',
    field_driver_phone: 'Driver phone',
    field_location_name: 'Location',
    field_address: 'Address',
    field_departure_time: 'Departure time',
    departure_time_hint: 'Usual departure time (HH:mm)',
    field_bus: 'Bus',
    field_location: 'Departure location',
    capacity_invalid: 'Between 1 and 150 seats',
    active: 'Active',
    inactive: 'Inactive',
    active_bus: 'Bus available for assignments',
    active_location: 'Offered to travellers on the website',
    seats_count: '{count} seats',
    bus: 'Bus',
    no_driver: 'No driver set',
    pick_event: 'Select a pilgrimage',
    assign_bus: 'Assign a bus',
    assign: 'Assign',
    unassign: 'Remove',
    unassign_title: 'Remove this bus?',
    total_seats: 'Seats',
    total_travellers: 'Confirmed travellers',
    buses_assigned: 'Buses',
    travellers_hint: 'Travellers are counted from confirmed website reservations at each pick-up point.',
    group_subtitle: '{buses} bus(es) assigned',
    capacity_line: '{passengers} travellers / {seats} seats',
    seats_missing: '{count} seats short',
    seats_ok: '{count} seats free',
    assignments_empty: 'No buses assigned yet',
    assignments_empty_body: 'Assign buses to the pick-up points of {name}.',
    no_free_bus: 'Every active bus is already assigned to this pilgrimage.',
    no_active_location: 'Add an active departure location first.',
    already_assigned: 'This bus is already assigned to this pilgrimage.',
    toast_bus_saved: 'Bus {plate} saved',
    toast_location_saved: 'Location {name} saved',
    toast_assigned: 'Bus assigned',
    toast_deleted: 'Deleted',
    delete_title: 'Delete?',
    delete_bus_body: 'Bus {plate} will be removed.',
    delete_location_body: 'Location {name} will be removed.',
    delete_assignment_body: 'Bus {plate} will be removed from this pilgrimage.',
  },
  documents_page: {
    title: 'Minutes & reports',
    subtitle: 'Parish records',
    search_placeholder: 'Search title, content...',
    filter_category: 'All categories',
    new_document: 'New document',
    edit_document: 'Edit document',
    field_title: 'Title',
    field_category: 'Category',
    field_meeting_date: 'Meeting date',
    field_location: 'Location',
    field_attendees: 'Attendees',
    field_author: 'Author',
    field_content: 'Content',
    attendees_hint: 'Optional — names separated by commas',
    content_placeholder: 'Write the minutes or report here...',
    content_required: 'The document cannot be empty',
    no_content: 'No content',
    updated_on: 'Updated {date}',
    print: 'Print / Save as PDF',
    categories: {
      minutes: 'Minutes',
      report: 'Report',
      announcement: 'Announcement',
      other: 'Other',
    },
    toast_created: '"{title}" created',
    toast_updated: '"{title}" saved',
    toast_deleted: 'Document deleted',
    delete_confirm_title: 'Delete this document?',
    delete_confirm_body: '"{title}" will be permanently deleted.',
    empty_title: 'No documents yet',
    empty_body: 'Record meeting minutes, reports and announcements here.',
  },
  editor: {
    toolbar: 'Formatting',
    bold: 'Bold',
    italic: 'Italic',
    underline: 'Underline',
    strike: 'Strikethrough',
    heading: 'Heading {level}',
    bullet_list: 'Bulleted list',
    ordered_list: 'Numbered list',
    blockquote: 'Quote',
    horizontal_rule: 'Divider',
    link: 'Link',
    remove_link: 'Remove link',
    undo: 'Undo',
    redo: 'Redo',
  },
  validation: {
    required: 'This field is required',
    invalid_email: 'Invalid email',
    invalid_phone: 'Invalid phone number',
    min_length: 'Minimum {count} characters',
    number_required: 'Must be a number',
  },
};

export default en;
